import styled from 'styled-components';
import { FormattedWidgetFontSize } from '../types';

/**
 * This approach is taken from an article by kizu.dev, a
 * member of the CSS working group. You can read the article here
 * https://kizu.dev/fit-to-width/
 *
 * While this is a bit hacky, it has the advantage of not relying on JS
 * to adjust font size, which was the previous way of solving this,
 * and caused overflow issues. Plus, the @property CSS rule is
 * supported in all major browsers.
 */

const TextFitContainer = styled.span`
  display: flex;
  container-type: inline-size;
  align-items: center;
  max-height: ${FormattedWidgetFontSize.XL}px;

  --captured-length: initial;
  --support-sentinel: var(--captured-length, 9999px);

  & > [aria-hidden] {
    visibility: hidden;
  }

  & > :not([aria-hidden]) {
    flex-grow: 1;
    container-type: inline-size;

    --captured-length: 100cqi;
    --available-space: var(--captured-length);

    & > * {
      --support-sentinel: inherit;
      --captured-length: 100cqi;
      --ratio: tan(
        atan2(
          var(--available-space),
          var(--available-space) - var(--captured-length)
        )
      );
      --font-size: clamp(
        1em,
        1em * var(--ratio),
        ${FormattedWidgetFontSize.XL}px - var(--support-sentinel)
      );
      inline-size: var(--available-space);

      &:not(.text-fit) {
        display: flex;
        justify-content: center;
        font-size: var(--font-size);

        @container (inline-size > 0) {
          white-space: nowrap;
        }
      }
    }
  }

  @property --captured-length {
    syntax: '<length>';
    initial-value: 0px;
    inherits: true;
  }

  @property --captured-length2 {
    syntax: '<length>';
    initial-value: 0px;
    inherits: true;
  }
`;

export const TextFit = ({ value }: { value: string }) => (
  <TextFitContainer>
    <span>
      <span>{value}</span>
    </span>
    <span aria-hidden="true">{value}</span>
  </TextFitContainer>
);
